import React, { useEffect, useState, useCallback, useMemo, ComponentType, FC, HTMLAttributes, memo, useRef, ReactNode } from 'react';
import { styled } from '@mui/system';

import { RootState, useAppSelector } from '@/state/store';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { item, logo, contents, close, thumbnail } from  '@/loaders/project';
import { update, setLogo, remove, clearLogo, leave, clearThumbnails } from '@/actions/project';
import RequestStatus from '@/contracts/request-status.enum';
import Layout from '../Layout';
import { Section } from '@/state/types/section';
import ModelUploadDialog from './ModelUploadDialog';
import ProjectMembers from './ProjectMembers';
import Title from '@/components/Title';
import CenteringWrapper from '@/components/CenteringWrapper';

import LeftSide from '@/components/LeftSide';
import RightSide from '@/components/RightSize';
import Tabs, { TabsProps } from '@/components/Tabs';
import Panel from '@/components/Panel';
import GearIcon from '@/components/icons/Gear';
import CrossIcon from '@/components/icons/Cross';

import { AccessLevel, UserStatus } from '@bimdata/definitions/const';
import { showMessage, exitPoint as setExitPoint } from '@/actions/common';
import ProjectCommonForm, { ProjectFormData as ProjectCommonData } from './ProjectCommonForm';
import ProjectSettingsForm from './ProjectSettingsForm';
import { FormTab } from './common.controls';
import { createSelector } from '@reduxjs/toolkit';
import { saveImplicitProjectThumbnails, saveLastProjectUpdateTab } from '@/actions/client';
import UsersIcon from '@/components/icons/Users';
import InviteParticipantDialog from './InviteParticipantDialog';
import InviteParticipantBtn from './InviteParticipantBtn';
import ProjectModels, { ProjectModelsProps } from './ProjectModels';
import ModelsSetup from '@/components/icons/ModelsSetup';
import { SvgIconProps } from '@mui/material';
import UploadModelBtn from './UploadModelBtn';
import NoModelsPlaceholder from './NoModelsPlaceholder';
import PanelButton from '@/components/PanelButton';
import ModelDropCurtain from './ModelDropCurtain';
import PageTitle from '@/components/PageTitle';
import ShareButton from './ShareButton';
import dropzone from '@/components/Dropzone';
import { ProjectData } from '@/state/types/project-data';
import { Profile } from '@bimdata/definitions/dto';
import t from '@/utils/translate';
import Prompt from '@/components/Prompt';
import { FileAccumulator } from './FileAccumulator';
import { ApiError } from '@bimdata/client';

// @ts-ignore: Unreachable code error
const LOGO_SIZE_LIMIT = +import.meta.env.VITE_AVA_SIZE_LIMIT_KB || 512;

const TitleText = styled('span')`
  font-size: 17px;
  font-weight: 500;
  padding-left: 2px;
`;

function TabIconStyled<T extends SvgIconProps>(Component: ComponentType<T>): FC<T> {
  return styled(Component)({
    verticalAlign: 'middle',
    lineHeight: '18px',
    marginRight: '5px',
    width: '18px',
    height: '18px',
  });
}

const ParticipantsTabIcon = TabIconStyled(UsersIcon);

const ModelsSetupIcon = TabIconStyled(ModelsSetup);

const Banner = styled('div')(`
  margin: auto;
  color: #a2a2a2;
`);

const PageSkeleton = styled('div')({
  position: 'fixed',
  top: '0px',
  left: '0px',
  right: '0px',
  bottom: '0px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyItems: 'center',
  alignContent: 'center',
  justifyContent: 'center',
  fontFamily: 'Roboto',
  fontSize: '16px',
  fontWeight: 400,
  color: '#a2a2a2',
});

enum Subsections {
  COMMON = 'general',
  MODELS = 'models',
  PARTICIPANTS = 'members',
  SETTINGS = 'import',
};

const FormTabDropZone = dropzone<HTMLAttributes<HTMLDivElement>>(FormTab);

type ModelsTabProps = {
  project: ProjectData;
  profile: Profile;
  onUploadClaim: (files?: File[]) => void;
};

const allowedFiles = ['.rvt', '.bim'];

const ProjectModelsMemoized = memo(({ project, currentUser, claimUpload }: ProjectModelsProps & { claimUpload: () => void}) => {
  const EmptyProjectPlaceholder = useMemo(() => 
    (project.access === AccessLevel.READER)
      ? (<Banner>{t('project.message.empty', true)}</Banner>)
      : (<NoModelsPlaceholder onBtnClick={claimUpload}/>),
    [project.access, claimUpload]
  );
  return <ProjectModels project={project} currentUser={currentUser} placeholder={EmptyProjectPlaceholder} />
}, (prev, next) => (prev.project === next.project) && (prev.currentUser === next.currentUser) && (prev.claimUpload === next.claimUpload));

const ModelsTab = ({ project, profile, onUploadClaim }: ModelsTabProps) => {
  const [dragging, setDragging] = useState(false);

  const currentUser = useMemo(() => ({
    ...profile,
    fullName: t('user.you'),
    status: UserStatus.ACTIVE,
  }), [profile.id]);

  const claimUpload = useCallback((files?: File[]) => {
    onUploadClaim(files);
  }, [onUploadClaim]);

  const dragEnterHandler = useCallback(() => {
    setDragging(true);
  }, []);

  const dragLeaveHandler = useCallback(() => {
    setDragging(false);
  }, []);

  const dropHandler = useCallback((files: File[]) => {
    setDragging(false);
    claimUpload(files);
  }, [claimUpload]);

  return (
    <FormTabDropZone
      accept={allowedFiles}
      onFileDrop={dropHandler}
      onDragEnter={dragEnterHandler}
      onDragLeave={dragLeaveHandler}
      noClick={true}
    >
      <ProjectModelsMemoized project={project} currentUser={currentUser} claimUpload={claimUpload}/>
      {!!dragging && <ModelDropCurtain />}
    </FormTabDropZone>
  );
};

const ADMINS = [AccessLevel.ADMIN, AccessLevel.OWNER];

const MEMBERSHIP_MANAGERS = [AccessLevel.ADMIN, AccessLevel.OWNER];

const SHARE_SUBSECTIONS: string[] = [Subsections.PARTICIPANTS, Subsections.COMMON];

const WRITER_SUBSECTIONS: string[] = [Subsections.PARTICIPANTS, Subsections.MODELS];

export default () => {
  const { id, subsection } = useParams();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);

  const { current: accumulator } = useRef<FileAccumulator>(new FileAccumulator());
  
  const [projectRemoving, setProjectRemoving] = useState(false);
  const [projectLeaving, setProjectLeaving] = useState(false);
  const [inviteDlgOpen, setInviteDlgOpen] = useState<boolean>(false);

  const {
    profile, current, status, openingError, logos, thumbnails, ImplicitProjectThumbnails, exitPoint, lastProjectUpdateTab
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.profile,
        (state: RootState) => state.projects.current,
        (state: RootState) => state.projects.openingStatus,
        (state: RootState) => state.projects.openingError,
        (state: RootState) => state.projects.logos,
        (state: RootState) => state.projects.thumbnails,
        (state: RootState) => state.authentication.clientState.ImplicitProjectThumbnails,
        (state: RootState) => state.common.exitPoint,
        (state: RootState) => state.authentication.clientState.lastProjectUpdateTab,
      ],
      (profile, current, status, openingError, logos, thumbnails, ImplicitProjectThumbnails, exitPoint, lastProjectUpdateTab) => ({
        profile, current, status, openingError, logos, thumbnails, ImplicitProjectThumbnails, exitPoint, lastProjectUpdateTab
      })
    )
  );

  const onTabSelect = useCallback((v: string) => {
    saveLastProjectUpdateTab(v);
    navigate(`/project/${id}/settings/${v}`);
  }, [ id ]);

  const reload = useCallback(async () => await (current && contents(current.code)), [current]);

  const deleteProject = useCallback(async () => {
    if (current) {
      await remove(current.code);
    }
    navigate('/project');
  }, [current]);

  const onUploadClaim = useCallback((files: File[]) => {
    if (files && files.length) {
      files.forEach((f) => {
        accumulator.add(f);
      });
    }
    setUploadOpen(true);
  }, []);

  const onCommonFormSubmit = useCallback(
    async ({
      name, implicitProjectThumbnails, logoPath, logoFile, thumbnail
    }: ProjectCommonData) => {
      if (!current) {
        return;
      }

      if (name !== current.name) {
        await update(current.code, { name });
      }

      if (
        !ImplicitProjectThumbnails[current.code]
        || implicitProjectThumbnails !== ImplicitProjectThumbnails[current.code]?.enabled
      ) {
        await saveImplicitProjectThumbnails(current.code, { enabled: implicitProjectThumbnails });
      }

      if (thumbnails[current.code]?.source && thumbnail === null) {
        await clearThumbnails(current.code);
      }

      if (logoPath !== logos[current.code].source) {
        if (!logoFile) {
          await clearLogo(current.code);
          return;
        }
        if (logoFile.size > LOGO_SIZE_LIMIT * 1024) {
          showMessage(t('project.error.thumbSize', { limit: LOGO_SIZE_LIMIT }), 'error');
          logo(current);
        } else {
          await setLogo(current.code, logoFile);
        }
      }
    },
    [ current, ImplicitProjectThumbnails, logos, thumbnails ]
  );

  const leaveProject = useCallback(async () => {
    if (current) {
      await leave(current.code);
    }
    navigate('/project');
  }, [current]);

  const exit = useCallback(() => {
    if (exitPoint) {
      navigate(exitPoint);
      setExitPoint(null);
    } else {
      navigate('./../..');
    }
  }, [exitPoint]);

  useEffect(() => {
    return () => {
      close();
    }
  }, [id]);

  useEffect(() => {
    if ((!current || current.code != id) && !projectRemoving && !projectLeaving) {
      item(id!);
    }
  }, [id, current, projectRemoving]);

  useEffect(() => {
    if (current && status === RequestStatus.IDLE) {
      if (
        !thumbnails[current.code]
        && ImplicitProjectThumbnails[current.code]?.small
      ) {
        thumbnail(current.code, ImplicitProjectThumbnails[current.code].small!);
      }
      if (!logos[current.code]) {
        logo(current);
      }
    }
  }, [ status, current ]);

  if (!id) throw new Error('Project id not specified');

  const tabs = useMemo<TabsProps['tabs']>(() => {
    if (!current) return [];
    const isAdmin = ADMINS.includes(current.access);
    const result: TabsProps['tabs'] = [];

    if (MEMBERSHIP_MANAGERS.includes(current.access)) {
      result.push(
        {
          key: Subsections.PARTICIPANTS,
          title: t('project.title.participants'),
          icon: (<ParticipantsTabIcon style={{ width: '18px', height: '18px' }} />)
        }
      );
    }

    if (current.access !== AccessLevel.READER) {
      result.push(
        {
          key: Subsections.MODELS,
          title: t('project.title.models'),
          icon: (<ModelsSetupIcon style={{ width: '18px', height: '18px' }} />)
        }
      );
    }

    if (isAdmin) {
      result.unshift(
        {
          key: Subsections.COMMON,
          title: t('project.title.general'),
        }
      );
      result.push(
        {
          key: Subsections.SETTINGS,
          title: t('project.title.settings'),
        }
      );
    }
    return result;
  }, [current]);

  const loaded = current && current.code === id;

  if (
    !loaded
    || !logos[current.code]
    || logos[current.code].status === RequestStatus.BUSY
    || (!thumbnails[current.code] && ImplicitProjectThumbnails[current.code]?.small)
    || thumbnails[current.code]?.status === RequestStatus.BUSY
  ) {
    let banner: string | ReactNode = t('state.loading');
    if (status === RequestStatus.ERROR) {
      banner = t('project.error.loadFailed', true);

      if (openingError instanceof ApiError && (openingError.code === 403 || openingError.code === 404)) {
        banner = t('project.error.notFoundOrAccessDenied', true);
      }
    }

    return <Layout>
      <CenteringWrapper top={60}>
        <Banner>
          {banner}
        </Banner>
      </CenteringWrapper>
    </Layout>;
  }

  const views = Array.from(
    current?.models?.reduce(
      (opts: Set<string> | null, section: Section) => {
        section.models.forEach(({ views }) => {
          if (!opts) {
            opts = new Set(views);
          } else {
            for (let v of opts) {
              if (!views.includes(v)) {
                opts.delete(v);
              }
            }
          }
        });
        return opts;
      },
      null
    ) || new Set<string>()
  );

  if (current.access === AccessLevel.READER) {
    return <Navigate to={`./../${subsection ? '../' : ''}`} replace/>;
  }

  if (!subsection) {
    if (lastProjectUpdateTab) {
      return <Navigate to={`./${lastProjectUpdateTab}`} replace/>;
    }

    if (MEMBERSHIP_MANAGERS.includes(current.access)) {
      return <Navigate to={`./${Subsections.PARTICIPANTS}`} replace/>;
    }
    if (tabs.length) {
      return <Navigate to={`./${tabs[0].key}`} replace/>;
    }
    return <Navigate to="./../" replace/>;
  } else if (!Object.values<string>(Subsections).includes(subsection)) {
    return <Navigate to="./../" replace/>;
  }

  if (current.access === AccessLevel.WRITER && !WRITER_SUBSECTIONS.includes(subsection || '')) {
    return <Navigate to={`./../${Subsections.MODELS}`} replace/>;
  }

  // onReset={() => navigate(`/project/${id}`)}

  /*
    {current.access !== AccessLevel.READER && (
      <HeaderButton onClick={ () => setUploadOpen(true) } title={t('project.action.upload')}>
        <ImportModelsIcon style={{ fontSize: '18px'}} />
      </HeaderButton>
    )}

  */

  return (<>
    <Layout>
      <Title id="title" style={{ cursor: 'pointer' }} onClick={ () => navigate(`../${id}`) }>{ current?.name }</Title>

      <Panel margin-h="medium" text-align="center">
        <LeftSide><GearIcon size={24} fill='#6B778C' style={{ margin: '6px' }}/><TitleText>{t('project.action.setting')}</TitleText></LeftSide>
        <RightSide>
          <PanelButton onClick={exit}>
            <CrossIcon size={18} />
          </PanelButton>
        </RightSide>
      </Panel>

      <Panel>
        <LeftSide>
          <Tabs
            value={subsection}
            tabs={tabs}
            onTabSelect={onTabSelect}
          />
        </LeftSide>
        <RightSide>
          {(subsection === Subsections.PARTICIPANTS && !!(current.participants && current.invitations) && (MEMBERSHIP_MANAGERS.includes(current.access))) && (
            <InviteParticipantBtn onClick={() => setInviteDlgOpen(true)} />
          )}
          {(subsection === Subsections.MODELS && !!current.models) && (
            <UploadModelBtn project={current.code} onClick={() => setUploadOpen(true)} />
          )}
          {!!current && current.access === AccessLevel.OWNER && (SHARE_SUBSECTIONS.includes(subsection)) && (
            <ShareButton project={current} />
          )}
        </RightSide>
      </Panel>

      {subsection === Subsections.COMMON && (
        <FormTab>
          <PageTitle>{current && `${t('project.title.general')} — ${current.name} — BIMDATA`}</PageTitle>
          <ProjectCommonForm
            submitTitle={t('project.action.save')}
            onSubmit={onCommonFormSubmit}
            initialValues={{
              name: current.name,
              logo: logos[current.code].source,
              implicitProjectThumbnails: (typeof ImplicitProjectThumbnails[current.code]?.enabled === 'undefined')
                ? true
                : !!ImplicitProjectThumbnails[current.code]?.enabled,
              thumbnail: thumbnails[current.code]?.source
            }}
            canDelete={current.access === AccessLevel.OWNER || profile?.isSuperadmin}
            onDelete={() => setProjectRemoving(true)}
          />

          {(current.access === AccessLevel.OWNER || profile?.isSuperadmin) && (
            <Prompt
              open={projectRemoving}
              title={t('project.title.confirmDeletion')}
              question={t('project.hint.confirm.delete', { name: current.name }, true)}
              onClose='cancel'
              onEnter='confirm'
              actions={[
                {
                  name: 'confirm',
                  handler: deleteProject,
                  caption: t('common.action.delete'),
                  style: {
                    background: '#DE350B',
                    color: '#FFFFFF',
                    border: 'none',
                  }
                },
                {
                  name: 'cancel',
                  handler: () => setProjectRemoving(false),
                  caption: t('common.action.cancel'),
                }
              ]}
            />
          )}
        </FormTab>
      )}

      {subsection === Subsections.SETTINGS && (
        <FormTab>
          <PageTitle>{current && `${t('project.title.settings')} — ${current.name} — BIMDATA`}</PageTitle>
          <ProjectSettingsForm
            views={views}
            submitTitle={t('project.action.save')}
            onSubmit={async ({ revit3DView, importRevitEdges }) => {
              if (revit3DView !== current.revit3DView || importRevitEdges !== current.importRevitEdges) {
                const datas = {
                  ...(revit3DView !== current.revit3DView ? { revit3DView }: {}),
                  ...(importRevitEdges !== current.importRevitEdges ? { importRevitEdges } :{})
                };
                await update(current.code, datas);
              }
            }}
            initialValues={{
              revit3DView: current.revit3DView,
              importRevitEdges: current.importRevitEdges,
              importCurves: true,
            }}
          />
        </FormTab>
      )}

      {(subsection === Subsections.MODELS) && (
        <>
          <PageTitle>{current && `${t('project.title.models')} — ${current.name} — BIMDATA`}</PageTitle>
          <ModelsTab project={current} profile={profile!} onUploadClaim={onUploadClaim} />
        </>
      )}

      {(subsection === Subsections.PARTICIPANTS) && (
        <FormTab>
          <PageTitle>{current && `${t('project.title.participants')} — ${current.name} — BIMDATA`}</PageTitle>
          {(status === RequestStatus.IDLE) && MEMBERSHIP_MANAGERS.includes(current.access) && (
            <ProjectMembers
              currentUser={{
                ...profile!,
                fullName: t('user.you'),
                status: UserStatus.ACTIVE,
              }}
              projectId={id!}
              ownershipManagement={current.access === AccessLevel.OWNER}
              placeholder={
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <InviteParticipantBtn onClick={() => setInviteDlgOpen(true)} />
                </div>
              }
            />
          )}
          {/*
          {(current.access !== AccessLevel.OWNER) && (<>
            <Container margin-v='medium' textAlign='center'>
              <Button
                variant="contrast-text"
                color="error"
                size="large"
                title={t('project.action.leave')}
                onClick={() => setProjectLeaving(true)}
              >
                {t('project.action.leave')}
              </Button>
            </Container>

            <ConfirmationDialog
              title={t('project.hint.confirm.leave')}
              details={current.name}
              isOpen={projectLeaving}
              onConfirm={leaveProject}
              onClose={() => setProjectLeaving(false)}
            />
          </>)}
          */}
        </FormTab>
      )}

      <ModelUploadDialog
        project={current}
        open={uploadOpen}
        files={(uploadOpen && !accumulator.empty) ? accumulator.files : undefined}
        onClose={() => {
          setUploadOpen(false);
          accumulator.dispose();
          reload();
        }}
      />

      <InviteParticipantDialog
        project={current}
        open={inviteDlgOpen}
        onClose={() => { setInviteDlgOpen(false); }}
      />
    </Layout>
  </>);
};
